import { useEffect, useMemo, useState } from 'react';
import type {
  AnalystRunGroup,
  HistorySummarizationEntry,
  IterationUsageEntry,
  IterationUsageTab,
} from '../../types/iteration-usage';
import {
  ITERATION_USAGE_TAB_LABELS,
  buildIterationUsageView,
  formatAnalystRunHeader,
  formatFlowAsJson,
  formatFlowAsMarkdown,
  formatArgsForModal,
  formatArgsInline,
  formatHistorySummarizationLine,
  formatIterationToolAction,
  formatAnalystRoundLine,
  formatIterationUsageLine,
  formatLlmRequestForModal,
  formatResultForModal,
  formatTotalsLine,
} from '../../types/iteration-usage';
import { checkIcon, chevronRightIcon, copyIcon } from '../icons';

type UsageTab = IterationUsageTab;

const TAB_STORAGE_KEY = 'mcp-iteration-usage-tab';

function readStoredTab(): UsageTab {
  try {
    const stored = window.localStorage.getItem(TAB_STORAGE_KEY);
    return stored === 'main' || stored === 'analyst' ? stored : 'combined';
  } catch {
    return 'combined';
  }
}

function storeTab(tab: UsageTab) {
  try {
    window.localStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    // Storage blocked (private mode, sandboxed embed) - the tab just isn't remembered
  }
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    navigator.clipboard?.writeText(text)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => undefined);
  };

  return (
    <button
      type="button"
      className="iteration-usage-copy-btn"
      onClick={handleClick}
      title={label}
      aria-label={label}
      dangerouslySetInnerHTML={{ __html: copied ? checkIcon : copyIcon }}
    />
  );
}

/** Copies the whole flow of the active tab; the text is built only on click (payloads can be large). */
function CopyFlowButton({ label, getText }: { label: string; getText: () => string }) {
  const [copied, setCopied] = useState(false);

  const handleClick = () => {
    navigator.clipboard?.writeText(getText())
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => undefined);
  };

  return (
    <button type="button" className="iteration-usage-copy-flow-btn" onClick={handleClick}>
      <span dangerouslySetInnerHTML={{ __html: copied ? checkIcon : copyIcon }} />
      {copied ? 'Copied' : label}
    </button>
  );
}

/** "Sent to LLM" + the tool list of one LLM call - shared by main runs and analyst rounds. */
function RunDetails({ entry, keyPrefix }: { entry: IterationUsageEntry; keyPrefix: string }) {
  return (
    <>
      <div className="iteration-usage-section">
        <div className="iteration-usage-tool-expanded-header">
          <span className="iteration-usage-section-title">Sent to LLM</span>
          <CopyButton text={formatLlmRequestForModal(entry.llmRequestPayload)} label="Copy request" />
        </div>
        <pre className="iteration-usage-llm-request">
          {formatLlmRequestForModal(entry.llmRequestPayload)}
        </pre>
      </div>

      {!!entry.llmResponseText && (
        <div className="iteration-usage-section">
          <div className="iteration-usage-tool-expanded-header">
            <span className="iteration-usage-section-title">LLM response</span>
            <CopyButton text={entry.llmResponseText} label="Copy response" />
          </div>
          <pre className="iteration-usage-llm-request">{entry.llmResponseText}</pre>
        </div>
      )}

      <div className="iteration-usage-section">
        <div className="iteration-usage-section-title">Tools</div>
        {!entry.tools.length ? (
          <div className="iteration-usage-run-empty">No tool calls in this run</div>
        ) : (
          <ul className="iteration-usage-tool-list">
            {entry.tools.map((tool, index) => (
              <li key={`${keyPrefix}-${tool.toolName}-${index}`} className="iteration-usage-tool-item">
                <details className="iteration-usage-tool-details">
                  <summary className="iteration-usage-tool-summary" title="Click to view the response">
                    <div className="iteration-usage-tool-summary-row">
                      <span
                        className="iteration-usage-tool-chevron"
                        dangerouslySetInnerHTML={{ __html: chevronRightIcon }}
                      />
                      <span className={`iteration-usage-tool-name${tool.success ? '' : ' iteration-usage-tool-error'}`}>
                        {formatIterationToolAction(tool)}
                      </span>
                    </div>
                    <div className="iteration-usage-tool-summary-row iteration-usage-tool-summary-row-args">
                      <span
                        className="iteration-usage-tool-args-inline"
                        title={formatArgsForModal(tool.argsSummary)}
                      >
                        {formatArgsInline(tool.argsSummary)}
                      </span>
                      <CopyButton text={formatArgsForModal(tool.argsSummary)} label="Copy request" />
                    </div>
                  </summary>
                  <div className="iteration-usage-tool-expanded">
                    <div className="iteration-usage-tool-expanded-header">
                      <span className="iteration-usage-section-title">Request</span>
                      <CopyButton text={formatArgsForModal(tool.argsSummary)} label="Copy request" />
                    </div>
                    <pre className="iteration-usage-tool-response">{formatArgsForModal(tool.argsSummary)}</pre>
                    <div className="iteration-usage-tool-expanded-header">
                      <span className="iteration-usage-section-title">Response</span>
                      <CopyButton text={formatResultForModal(tool.resultSummary)} label="Copy response" />
                    </div>
                    <pre className="iteration-usage-tool-response">{formatResultForModal(tool.resultSummary)}</pre>
                  </div>
                </details>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

/** One Data_Analyst call: a collapsible block with each of the sub-agent's rounds. */
function AnalystRunBlock({ group, defaultOpen }: { group: AnalystRunGroup; defaultOpen: boolean }) {
  return (
    <details className="iteration-usage-analyst-run" open={defaultOpen}>
      <summary className="iteration-usage-analyst-run-header">
        <span className="iteration-usage-source-badge iteration-usage-source-badge-analyst">Analyst</span>
        {formatAnalystRunHeader(group)}
      </summary>
      {group.rounds.map((round) => (
        <div key={`${group.key}-${round.iteration}`} className="iteration-usage-analyst-round">
          <div className="iteration-usage-run-header">
            {formatAnalystRoundLine(round)}
          </div>
          <RunDetails entry={round} keyPrefix={`${group.key}-${round.iteration}`} />
        </div>
      ))}
    </details>
  );
}

export function IterationUsageModal({
  entries,
  summarizationEvents,
  onClose,
}: {
  entries: IterationUsageEntry[];
  summarizationEvents?: HistorySummarizationEntry[];
  onClose: () => void;
}) {
  const view = useMemo(() => buildIterationUsageView(entries), [entries]);
  const hasAnalyst = view.analystRuns.length > 0;
  const [tab, setTab] = useState<UsageTab>(readStoredTab);
  const activeTab: UsageTab = hasAnalyst ? tab : 'combined';

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const selectTab = (next: UsageTab) => {
    setTab(next);
    storeTab(next);
  };

  const renderMainRun = (run: (typeof view.mainRuns)[number], withAnalysts: boolean) => (
    <div key={`main-${run.position}-${run.entry.iteration}`} className="iteration-usage-run">
      <div className="iteration-usage-run-header">
        {hasAnalyst && <span className="iteration-usage-source-badge">Main</span>}
        {formatIterationUsageLine(run.entry, run.position)}
      </div>
      <RunDetails entry={run.entry} keyPrefix={`main-${run.position}`} />
      {withAnalysts && run.analystRuns.map((group) => (
        <AnalystRunBlock key={group.key} group={group} defaultOpen={false} />
      ))}
    </div>
  );

  return (
    <div className="iteration-usage-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="iteration-usage-modal"
        role="dialog"
        aria-modal="true"
        aria-label="LLM run token usage"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="iteration-usage-modal-header">
          <h4 className="iteration-usage-modal-title">LLM runs & token usage</h4>
          <div className="iteration-usage-header-actions">
            <CopyFlowButton
              label="Copy as text"
              getText={() => formatFlowAsMarkdown(entries, activeTab, summarizationEvents)}
            />
            <CopyFlowButton
              label="Copy as JSON"
              getText={() => formatFlowAsJson(entries, activeTab, summarizationEvents)}
            />
            <button type="button" className="iteration-usage-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>
        {hasAnalyst && (
          <div className="iteration-usage-tabs-bar">
            <div className="iteration-usage-tabs" role="tablist" aria-label="Agent filter">
              {(Object.entries(ITERATION_USAGE_TAB_LABELS) as Array<[UsageTab, string]>).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === value}
                  className={`iteration-usage-tab${activeTab === value ? ' iteration-usage-tab-active' : ''}`}
                  onClick={() => selectTab(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="iteration-usage-totals">
              <span>{formatTotalsLine('Main', view.mainTotals)}</span>
              <span>{formatTotalsLine('Analyst', view.analystTotals)}</span>
            </div>
          </div>
        )}
        <div className="iteration-usage-modal-body">
          {activeTab !== 'analyst' && !!summarizationEvents?.length && (
            <div className="iteration-usage-run">
              <div className="iteration-usage-run-header">History Summarization</div>
              {summarizationEvents.map((entry, index) => (
                <div key={index} className="iteration-usage-section">
                  <div
                    className={`iteration-usage-section-title${'failed' in entry ? ' iteration-usage-tool-error' : ''}`}
                  >
                    {formatHistorySummarizationLine(entry)}
                  </div>
                  {'narrativeSummary' in entry && entry.narrativeSummary && (
                    <details className="iteration-usage-tool-details">
                      <summary className="iteration-usage-tool-summary">View summary</summary>
                      <pre className="iteration-usage-llm-request">{entry.narrativeSummary}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeTab === 'combined' && (
            <>
              {view.mainRuns.map((run) => renderMainRun(run, true))}
              {view.orphanAnalystRuns.map((group) => (
                <AnalystRunBlock key={group.key} group={group} defaultOpen={false} />
              ))}
            </>
          )}

          {activeTab === 'main' && view.mainRuns.map((run) => renderMainRun(run, false))}

          {activeTab === 'analyst' && view.analystRuns.map((group) => (
            <AnalystRunBlock key={group.key} group={group} defaultOpen />
          ))}
        </div>
      </div>
    </div>
  );
}
